import { initializeDanRoberts } from '../data/personas/dan_roberts';
import { clearPersonaData, loadPersonaIntoStorage } from '../data/personas/helpers';
import type { MockPersonaData } from '../data/personas/types';

/**
 * PersonaService handles user profile and mock data management
 *
 * MOCK DATA LOADING:
 * - Development: Automatically loads Dan Roberts persona if no existing data
 * - Production: Skips mock data loading, users start with empty state
 * - Override: Set VITE_ENABLE_MOCK_PERSONA=true to force load in any environment
 *
 * This ensures production users don't get pre-populated with demo data
 * while maintaining development convenience.
 */
class PersonaService {
  private isInitialized: boolean = false;
  private currentPersona: MockPersonaData | null = null;

  /**
   * Check if persona data exists in localStorage
   */
  hasPersonaData(): boolean {
    const hasAspirations = localStorage.getItem('godmode_aspirations') !== null;
    const hasGoals = localStorage.getItem('godmode_goals') !== null;
    const hasLists = localStorage.getItem('godmode_lists') !== null;

    return hasAspirations || hasGoals || hasLists;
  }

  /**
   * Check if we should load mock persona data based on environment
   */
  private shouldLoadMockData(): boolean {
    // Only load mock data in development or when explicitly enabled
    const isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
    const isProduction = import.meta.env.PROD || import.meta.env.NODE_ENV === 'production';
    const mockDataEnabled = import.meta.env.VITE_ENABLE_MOCK_PERSONA === 'true';

    // In production, only load if explicitly enabled via environment variable
    if (isProduction && !mockDataEnabled) {
      return false;
    }

    // In development, always load unless explicitly disabled
    return isDevelopment || mockDataEnabled;
  }

  /**
   * Check if mock data loading is enabled
   */
  isMockDataEnabled(): boolean {
    return this.shouldLoadMockData();
  }

  /**
   * Initialize the app with persona data if needed
   */
  initializeIfNeeded(): void {
    if (this.isInitialized) {
      return;
    }

    // Check if we have any data in localStorage
    if (!this.hasPersonaData()) {
      // Only load mock data in development environment
      if (this.shouldLoadMockData()) {
        console.log('🎭 Development environment: Loading persona data...');
        this.loadPersona();
      } else {
        console.log('🏭 Production environment: Skipping mock persona data loading');
      }
    } else {
      console.log('📊 Existing data found in localStorage');
    }

    // In development, always ensure we have authentication test user data
    if (import.meta.env.DEV) {
      this.ensureDevUserProfile();
    }

    this.isInitialized = true;
  }

  /**
   * Ensure development user profile matches Dan Roberts persona
   */
  private ensureDevUserProfile(): void {
    const profile = this.getProfile();
    if (profile.email === 'dan.roberts@godmode.com') {
      console.log('👤 Development user profile confirmed');
    } else {
      // Update profile to match authentication config if needed
      const devProfile = {
        ...profile,
        email: 'dan.roberts@godmode.com',
        name: 'Dan Roberts'
      };
      localStorage.setItem('godmode_user_profile', JSON.stringify(devProfile));
      console.log('👤 Development user profile updated to match auth config');
    }
  }

  /**
   * Force load the persona data (useful for reset)
   */
  loadPersona(): void {
    console.log('🎭 Loading Dan the Developer persona...');

    // Clear any existing data
    clearPersonaData();

    // Get the persona data
    const personaData = initializeDanRoberts();

    // Load the persona data into localStorage
    loadPersonaIntoStorage(personaData);

    // Store the persona reference
    this.currentPersona = personaData;

    console.log('✅ Persona loaded successfully');
  }

  /**
   * Manually load persona data (useful for demos/testing in production)
   */
  forceLoadPersona(): void {
    console.log('🎭 Manually loading persona data...');
    this.loadPersona();
  }

  /**
   * Get the current persona data
   */
  getCurrentPersona(): MockPersonaData | null {
    if (!this.currentPersona) {
      this.currentPersona = initializeDanRoberts();
    }
    return this.currentPersona;
  }

  /**
   * Get the profile data from localStorage or persona
   */
  getProfile() {
    // First try to get from localStorage
    const storedProfile = localStorage.getItem('godmode_user_profile');
    if (storedProfile) {
      return JSON.parse(storedProfile);
    }
    
    // Otherwise get from current persona
    const persona = this.getCurrentPersona();
    return persona?.profile || {
      id: 'user_default',
      name: 'Default User',
      email: 'user@example.com',
      age: 25,
      jobRole: 'User',
      company: 'Company',
      industry: 'Industry',
      yearsExperience: 0,
      salary: 0,
      currency: 'USD',
      bio: 'Default user profile',
      timezone: 'UTC'
    };
  }

  /**
   * Reset all data and reload persona
   */
  resetToPersona(): void {
    console.log('🔄 Resetting to default persona...');
    
    // Clear all data
    clearPersonaData();
    
    // Reset initialization flag
    this.isInitialized = false;
    
    // Reload persona
    this.initializeIfNeeded();
    
    // Reload the page to refresh all stores
    window.location.reload();
  }

  /**
   * Clear all data without reloading persona
   */
  clearAllData(): void {
    console.log('🗑️ Clearing all data...');
    clearPersonaData();
    this.currentPersona = null;
    this.isInitialized = false;
  }

  /**
   * Export current data as JSON
   */
  exportData(): string {
    const data = {
      aspirations: JSON.parse(localStorage.getItem('godmode_aspirations') || '[]'),
      goals: JSON.parse(localStorage.getItem('godmode_goals') || '[]'),
      lists: JSON.parse(localStorage.getItem('godmode_lists') || '[]'),
      schedules: JSON.parse(localStorage.getItem('godmode_list_schedules') || '[]'),
      profile: JSON.parse(localStorage.getItem('godmode_user_profile') || '{}')
    };
    
    return JSON.stringify(data, null, 2);
  }

  /**
   * Import data from JSON
   */
  importData(jsonData: string): void {
    try {
      const data = JSON.parse(jsonData);
      
      if (data.aspirations) {
        localStorage.setItem('godmode_aspirations', JSON.stringify(data.aspirations));
      }
      if (data.goals) {
        localStorage.setItem('godmode_goals', JSON.stringify(data.goals));
      }
      if (data.lists) {
        localStorage.setItem('godmode_lists', JSON.stringify(data.lists));
      }
      if (data.schedules) {
        localStorage.setItem('godmode_list_schedules', JSON.stringify(data.schedules));
      }
      if (data.profile) {
        localStorage.setItem('godmode_user_profile', JSON.stringify(data.profile));
      }
      
      console.log('✅ Data imported successfully');
      
      // Reload the page to refresh all stores
      window.location.reload();
    } catch (error) {
      console.error('❌ Failed to import data:', error);
      throw new Error('Invalid JSON data');
    }
  }
}

// Create singleton instance
export const personaService = new PersonaService();

// Auto-initialize on first import
if (typeof window !== 'undefined') {
  personaService.initializeIfNeeded();
}